import { jsPDF } from "jspdf";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  TablePagination,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import useProduct from "../../Hooks/useProduct";
import { baseURL } from "../../Utils/API";
import { decryptedBody } from "../../Utils/constant";
import { countryCode } from "../../Utils/CountryCode";
import PageHeader from "../../Components/UI/PageHeader";
import SectionCard from "../../Components/UI/SectionCard";

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [addProduct, setAddProduct] = useState(false);
  const [productData, setProductData] = useState({ country_code: "+91" });
  const [qrURL, setQrURL] = useState("");
  const [qrID, setQrID] = useState("");
  const [qrMacId, setQrMacID] = useState("");
  const [qrModal, setQRModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  const ref = useRef();
  const isMobile = useMediaQuery("(max-width:600px)");

  const {
    getProducts,
    addProduct: addNewProduct,
    deleteProduct,
  } = useProduct();
  const productTypes = ["SalvusBW", "SalvusBP", "SalvusBC"];
  const productCategories = ["Electronics"];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    const prod = await getProducts();
    const temp = decryptedBody(prod.data.data);
    setProducts(temp);
  };

  // const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   setProductData((prev) => ({ ...prev, [name]: value }));
  // };
  const handleChange = (e) => {
  let { name, value } = e.target;

  if (name === "mac_id") {
    // Remove everything except hex characters
    value = value.replace(/[^a-fA-F0-9]/g, "").toUpperCase();

    // Add colon every 2 characters
    value = value.match(/.{1,2}/g)?.join(":") || "";

    // Limit to 6 pairs (12 chars → 17 with colons)
    value = value.substring(0, 17);
  }

  setProductData((prev) => ({ ...prev, [name]: value }));
};

  const saveProduct = async () => {
    try {
      await addNewProduct(productData);
      setAddProduct(false);
      fetchProducts();
      toast.success("🎉 Product added");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add product");
    }
  };

  const printQR = () => {
    const doc = new jsPDF({ unit: "cm", format: [4, 6] });
    const image = new Image();
    image.src = qrURL;
    image.onload = () => {
      const qrSize = 3.5;
      const qrX = (4 - qrSize) / 2;
      const qrY = 0.5;
      doc.addImage(image, "PNG", qrX, qrY, qrSize, qrSize);
      const baseFontSize = 8;
      const centerX = 2;
      const line1Y = qrY + qrSize + 0.3;
      const line2Y = line1Y + 0.4;
      doc.setFont("courier", "normal");
      const qrIDWidth = doc.getTextWidth(qrID);
      const macIDWidth = doc.getTextWidth(qrMacId);
      const maxWidth = Math.max(qrIDWidth, macIDWidth);
      const scaleFactor1 = baseFontSize * (maxWidth / qrIDWidth);
      const scaleFactor2 = baseFontSize * (maxWidth / macIDWidth);
      doc.setFontSize(scaleFactor1);
      doc.text(qrID, centerX, line1Y, { align: "center" });
      doc.setFontSize(scaleFactor2);
      doc.text(qrMacId, centerX, line2Y, { align: "center" });
      doc.autoPrint();
      window.open(doc.output("bloburl"), "_blank");
    };
  };

  const deleteProducts = async (id) => {
    try {
      const res = await deleteProduct(id);
      const msg =
        res?.data?.data?.message ||
        res?.data?.message ||
        "Product deleted";
      toast.success(msg);
    } catch (err) {
      // Avoid crashing the UI on API errors
      // eslint-disable-next-line no-console
      console.error("deleteProduct failed:", err);
      const msg =
        err?.response?.data?.message || "Failed to delete product";
      toast.error(msg);
    } finally {
      fetchProducts();
      setDeleteConfirm(false);
    }
  };

  const paginatedProducts = products.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      <PageHeader
        title="Products"
        subtitle="Create products, print QR labels, and manage inventory."
        actions={[
          { label: "Add product", key: "add", onClick: () => setAddProduct(true), color: "primary", variant: "filled" },
          { label: "Refresh", key: "refresh", onClick: () => fetchProducts() },
        ]}
      />

      <SectionCard title="Product list" subtitle={`Total: ${products.length}`}>
        <Box sx={{ overflowX: "auto" }}>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table sx={{ minWidth: 1000 }} size="small">
              <TableHead>
                <TableRow>
                  {[
                    "Product type",
                    "MAC ID",
                    "QR ID",
                    "Category",
                    "Color",
                    "Batch Number",
                    "Manufacturing Date",
                    "MRP",
                    "Version",
                    "QR",
                    "Barcode",
                    "Created At",
                    "Action",
                  ].map((header) => (
                    <TableCell key={header} sx={{ fontWeight: 800 }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProducts.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.mac_id}</TableCell>
                    <TableCell>{row.uniqueId}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.color}</TableCell>
                    <TableCell>{row.batch_number}</TableCell>
                    <TableCell>{moment(row.manufacturing_date).format("L")}</TableCell>
                    <TableCell>{row.mrp}</TableCell>
                    <TableCell>{row.version}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setQrURL(`${baseURL}/${row.qrPath}`);
                          setQrID(row.uniqueId);
                          setQrMacID(row.mac_id);
                          setQRModal(true);
                        }}
                      >
                        <img width={40} alt="QR" src={`${baseURL}/${row.qrPath}`} />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <img width={40} alt="Barcode" src={`${baseURL}/${row.barcodePath}`} />
                    </TableCell>
                    <TableCell>{moment(row.createdAt).format("LLL")}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setDeleteConfirm(true);
                          setDeleteId(row.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      No products found.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={products.length}
            page={currentPage}
            onPageChange={(e, newPage) => setCurrentPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
          />
        </Box>
      </SectionCard>

      {/* Add Product Dialog */}
      <Dialog
        open={addProduct}
        onClose={() => setAddProduct(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New Product</DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select name="type" onChange={handleChange}>
                  {productTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select name="category" onChange={handleChange}>
                  {productCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="MAC ID"
                fullWidth
                name="mac_id"
                value={productData.mac_id || ""}
                onChange={handleChange}
                inputProps={{ maxLength: 17 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Color"
                fullWidth
                name="color"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Version"
                fullWidth
                name="version"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Batch Number"
                fullWidth
                name="batch_number"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Country Code</InputLabel>
                <Select value={"+91"} disabled name="country_code">
                  {countryCode.map(({ code, country, flag }, i) => (
                    <MenuItem
                      key={i}
                      value={code}
                    >{`${code} ${country} ${flag}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="MRP"
                fullWidth
                name="mrp"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Manufacturing Date"
                  disableFuture
                  onChange={(newValue) => setProductData(prev => ({
                    ...prev,
                    manufacturing_date: moment(newValue).format()
                  }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                {/* <DatePicker
                  label="Manufacturing Date"
                  disableFuture
                  value={
                    productData.manufacturing_date
                      ? moment(productData.manufacturing_date)
                      : moment()
                  } // ✅ default today
                  onChange={(newValue) =>
                    setProductData((prev) => ({
                      ...prev,
                      manufacturing_date: moment(newValue).format(),
                    }))
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                /> */}
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ flexDirection: isMobile ? "column" : "row", gap: 1 }}
        >
          <Button variant="contained" onClick={saveProduct}>
            Save
          </Button>
          <Button variant="outlined" onClick={() => setAddProduct(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Modal */}
      <Dialog
        open={qrModal}
        onClose={() => setQRModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>QR Code</DialogTitle>
        <DialogContent
          ref={ref}
          sx={{ textAlign: "center", overflow: "hidden", pt: 1 }}
        >
          <Box
            component="img"
            src={qrURL}
            alt="QR"
            sx={{
              display: "block",
              mx: "auto",
              maxWidth: "100%",
              maxHeight: 280,
              width: "auto",
              height: "auto",
              objectFit: "contain",
            }}
          />
          <Typography variant="body2" sx={{ mt: 1, wordBreak: "break-all" }}>
            {qrID}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
            {qrMacId}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={printQR}>
            <LocalPrintshopIcon sx={{ mr: 1 }} />
            Print
          </Button>
          <Button variant="outlined" onClick={() => setQRModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          This action can't be reverted. Are you sure you want to delete this
          product?
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => deleteProducts(deleteId)}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </Button>
          <Button variant="outlined" onClick={() => setDeleteConfirm(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductList;
